/**
 * Menu Types
 *
 * TypeScript types for menu system matching Python API schemas.
 */

/**
 * Menu target types for links.
 */
export type MenuTarget = '_self' | '_blank' | '_parent' | '_top';

/**
 * Menu type codes matching database values.
 */
export type MenuTypeCode =
  | 'menu_top'
  | 'menu_principal'
  | 'menu_bottom'
  | 'menu_perfil';

/**
 * Menu status enum.
 */
export enum MenuStatus {
  INACTIVE = 0,
  ACTIVE = 1,
}

/**
 * Menu type entity.
 */
export interface MenuType {
  id: number;
  code: MenuTypeCode;
  name: string;
  status: number;
  datetimeInclusion: string;
  datetimeLastUpdate?: string;
  menuCount: number;
}

/**
 * Menu tree node - hierarchical menu item.
 */
export interface MenuTreeNode {
  id: number;
  label: string;
  url: string;
  icon?: string;
  target: MenuTarget;
  cssClass?: string;
  menuOrder: number;
  status: number;
  level: number;
  children: MenuTreeNode[];
}

/**
 * Menu tree response from API.
 */
export interface MenuTreeResponse {
  menuType: MenuType;
  tree: MenuTreeNode[];
  totalMenus: number;
}

/**
 * Menu list item (flat list).
 */
export interface MenuListItem {
  id: number;
  label: string;
  url: string;
  icon?: string;
  target: MenuTarget;
  menuOrder: number;
  status: number;
  isPublic: boolean;
  parentId?: number;
  level: number;
  childrenCount: number;
}

/**
 * Menu response (full details).
 */
export interface MenuResponse {
  id: number;
  label: string;
  url: string;
  icon?: string;
  target: MenuTarget;
  cssClass?: string;
  isPublic: boolean;
  menuTypeId: number;
  parentId?: number;
  contentId?: number;
  menuOrder: number;
  status: number;
  datetimeInclusion: string;
  datetimeLastUpdate?: string;
  menuType?: MenuType;
}

/**
 * Menu list response with pagination.
 * Uses PaginationMeta from person.ts for consistency.
 */
export interface MenuListResponse {
  items: MenuListItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

/**
 * Menu filter parameters.
 */
export interface MenuFilter {
  status?: number;
  menuTypeId?: number;
  parentId?: number;
  search?: string;
}

/**
 * Menu tree request parameters.
 */
export interface MenuTreeParams {
  menuTypeId?: number;
  rootId?: number;
  maxDepth?: number;
  status?: number;
  locale?: string;
}

/**
 * Frontend navigation item (transformed from API).
 */
export interface NavMenuItem {
  id: number;
  label: string;
  path: string;
  icon?: string;
  target: MenuTarget;
  isExternal: boolean;
  isLegacy: boolean;
  isGroup: boolean;
  children: NavMenuItem[];
  level: number;
  cssClass?: string;
}

/**
 * Rotas React disponíveis no App.tsx.
 * Usadas para validação e mapeamento automático.
 */
const REACT_ROUTES = new Set([
  '/',
  '/login',
  '/forgot-password',
  '/person',
  '/person/create',
  '/product',
  '/product/create',
  '/product/categories',
  '/product/brands',
  '/product/families',
  '/product/types',
  '/product/attributes',
  '/product/images',
  '/product/import-images',
  '/product/export-images',
  '/product/inventory',
  '/product/warehouses',
  '/content',
  '/content/create',
  '/content/categories',
  '/files',
  '/page',
  '/widget-demo',
  '/resource',
  '/resource/create',
  '/resource/dashboard',
  '/resource/list',
  '/resource/attributes',
  '/resource/checkins',
  '/resource/uploads',
  '/resource/map',
  '/resource/items',
  '/segmentation/regions',
  '/segmentation/segments',
  '/settings/address-types',
  '/settings/phone-types',
  '/settings/email-types',
  '/settings/person-types',
  '/settings/payment-methods',
  '/settings/shipment-methods',
  '/settings/menu-types',
  '/settings/auth-items',
  '/settings/currencies',
  '/settings/languages',
  '/settings/etl',
  '/settings/access/profiles',
  '/settings/access/assignments',
  '/profile',
  '/budget',
  '/budget/create',
  '/budget/approval',
  '/budget/transactions',
  '/budget/cost-centers',
  '/store',
  '/store/create',
  '/store/inventory-report',
  '/cart',
  '/checkout',
  '/sales',
  '/tracking',
  '/campaign',
  '/campaign/create',
  '/campaign/wizard',
  '/campaign/dashboard',
  '/campaign/attributes',
  '/campaign/attribute-types',
  '/campaign/jobs',
  '/campaign/import-jobs',
  '/campaign/terms',
  '/campaign/results',
  '/campaign/credits',
  '/campaign/memberships',
  '/campaign/modalities',
  '/campaign/performance',
  '/campaign/participants',
  '/campaign/score-lots/reversal',
  '/campaign/export',
  '/ranking',
  '/ranking/generate',
  '/ranking/jobs',
  // Contato/Push removido (TH-56761): motor tmf_contact deletado
  // Reports routes
  '/reports',
  '/reports/access-log',
  '/reports/content-access',
  '/reports/audit-log',
  '/reports/campaign-data',
  '/reports/sales',
  '/reports/person-data',
  '/reports/person-hierarchy',
  '/reports/person-segments',
  '/reports/products',
  '/reports/product-sales',
  '/budget/report',
  '/reports/cost-centers',
  '/reports/transactions',
  // Person Configuration routes (Cadastro menu)
  '/person/access',
  '/person/client-groups',
  '/person/hierarchical-org',
  '/person/hierarchical-levels',
  '/person/attributes',
  '/person/attribute-types',
  '/person/system-integrations',
  '/person/person-integrations',
  '/person/business-areas',
  '/person/corporation-structures',
  '/person/send-token-batch',
  '/person/latlng',
  '/person/image-types',
  '/person/relationship-types',
  '/person/family-relationship-types',
  // Quiz routes
  '/quiz',
  '/quiz/create',
  '/quiz/config',
  '/quiz/executions',
  '/quiz/answers',
  '/quiz/reports',
  // Import routes
  '/import',
  '/import/upload',
  '/import/files',
  '/import/logs',
  '/import/types',
  '/import/validations',
  '/import/fields',
  // Drawing routes (Sorteios)
  '/drawing',
  '/drawing/results',
  // Legacy module routes (em migracao)
  '/score',
  '/term',
  // Checkin routes
  '/checkin/map',
  // Admin routes
  '/admin/schema-editor',
  // Communication routes
  '/communication',
  '/communication/dashboard',
  '/communication/campaigns',
  '/communication/contact-lists',
  '/communication/settings',
  '/communication/templates',
  '/communication/conversations',
  '/communication/workflows',
  '/communication/usage',
  '/communication/quotas',
  '/communication/email-logs',
  // Organization routes
  '/organization/company',
  '/organization/organogram',
  // Settings routes (admin)
  '/function',
  '/settings/configurations',
  '/settings/cache',
  '/settings/themes',
  '/settings/email-logs',
  '/settings/menus',
  '/settings/jobs',
  '/settings/migrations',
  '/settings/push',
  '/settings/version',
  // Service Desk routes
  '/service-desk',
  '/service-desk/inbox',
  '/service-desk/tickets/create',
  '/service-desk/metrics',
  '/service-desk/settings',
  '/service-desk/templates',
  '/service-desk/portal',
  '/service-desk/portal/new',
  '/service-desk/portal/knowledge-base',
  // SAC routes
  '/sac',
  '/sac/categories',
  '/sac/sla',
  '/sac/metrics',
  '/sac/insights',
  '/sac/channels',
  '/sac/templates',
  '/sac/workflows',
]);

/**
 * Rotas React que aceitam parâmetros dinâmicos.
 * Usado para validar rotas com IDs ou códigos.
 */
export const REACT_ROUTE_PATTERNS = [
  /^\/resource\/\d+\/items$/, // /resource/:id/items
  /^\/resource\/items\/[\w-]+$/, // /resource/items/:code
  /^\/resource\/\d+$/, // /resource/:id
  /^\/person\/\d+$/, // /person/:id
  /^\/content\/\d+$/, // /content/:id
  /^\/campaign\/\d+$/, // /campaign/:id
  /^\/budget\/\d+$/, // /budget/:id
  /^\/store\/\d+$/, // /store/:id
  /^\/ranking\/\d+$/, // /ranking/:id
  /^\/quiz\/\d+$/, // /quiz/:id
  /^\/service-desk\/tickets\/[\w-]+$/, // /service-desk/tickets/:id
  /^\/service-desk\/portal\/tickets\/[\w-]+$/, // /service-desk/portal/tickets/:id
];

/**
 * Mapeamento de módulos Yii para rotas React base.
 * Usado para deduzir automaticamente a rota quando não há mapeamento explícito.
 */
const MODULE_TO_REACT_ROUTE: Record<string, string> = {
  // Módulos principais
  person: '/person',
  resource: '/resource',
  content: '/content',
  campaign: '/campaign',
  budget: '/budget',
  store: '/store',
  sale: '/sales',
  product: '/product',

  // Módulos de configuração/sistema
  access: '/settings',
  system: '/settings',
  currency: '/settings',
  messages: '/settings',
  migration: '/settings',

  // Módulos específicos
  segmentregion: '/segmentation',
  ranking: '/ranking',
  score: '/score',
  term: '/term',
  contact: '/sac', // motor tmf_contact removido (TH-56761) → SAC
  import: '/import',
  export: '/export',
  report: '/reports',
  quiz: '/quiz',
  test: '/test',
  etl: '/etl',
  pushnotification: '/notifications',
  dashboard: '/dashboard',
  file: '/files',

  // Aliases comuns
  site: '/',
  portal: '/',
};

/**
 * Mapeamento de actions Yii para sufixos de rota React.
 */
const ACTION_TO_SUFFIX: Record<string, string> = {
  admin: '',          // Lista principal
  index: '',          // Lista principal
  create: '/create',
  view: '',           // Detalhe (precisa de ID)
  update: '',         // Edição (precisa de ID)
  delete: '',         // Redireciona para lista
  manage: '',         // Gerenciamento
};

/**
 * URL mapping from legacy Yii routes to new React routes.
 * Keys are lowercase for case-insensitive matching.
 *
 * IMPORTANTE: Estas URLs são geradas pelo Yii a partir da tabela content_interface
 * no formato: /{control}/{action} ou /{module}/{controller}/{action}
 *
 * As URLs do banco NÃO podem ser alteradas - este mapeamento adapta para React.
 */
const LEGACY_URL_MAP: Record<string, string> = {
  // ============================================
  // HOME / PORTAL
  // ============================================
  '/': '/',
  '/site': '/',
  '/site/index': '/',
  '/portal': '/',
  '/salestatus': '/',
  '/site/backportal': '/',

  // ============================================
  // PERSON / CADASTRO (pessoa física e jurídica)
  // ============================================
  '/person/person': '/person',
  '/person/person/admin': '/person',
  '/person/person/create': '/person/create',
  '/person/person/view': '/person', // Redireciona para lista (ID dinâmico)
  '/person/person/update': '/person', // Redireciona para lista (ID dinâmico)
  '/person/person/persondata': '/person',
  '/person/person/updateperson': '/person',
  '/person/person/access': '/person',
  '/person/person/backaccess': '/person',
  '/person/person/managelatlng': '/person',
  '/person/person/searchzipcode': '/person',
  '/manageperson': '/person',
  '/manageindividualperson': '/person',
  '/register': '/person',

  // Person - Grupo de Cliente
  '/person/clientgroup': '/person/client-groups',
  '/person/clientgroup/admin': '/person/client-groups',
  '/person/clientgroup/create': '/person/client-groups',

  // Person - Cadastro de Registro
  '/person/register': '/person',
  '/person/register/admin': '/person',

  // Person - Corporação/Hierarquia
  '/person/corporation/corphierarchicallevel': '/person/hierarchical-levels',
  '/person/corporation/corphierarchicallevel/admin': '/person/hierarchical-levels',
  '/person/corporation/corpbusinessarea': '/person/business-areas',
  '/person/corporation/corpbusinessarea/admin': '/person/business-areas',
  '/person/corporation/corphierarchicalorganization': '/person/hierarchical-org',
  '/person/corporation/corphierarchicalorganization/admin': '/person/hierarchical-org',
  '/person/corporation/corphierarchicalstructurecorp': '/person/corporation-structures',
  '/person/corporation/corphierarchicalstructurecorp/admin': '/person/corporation-structures',

  // Person - Tipos (Settings)
  '/person/addresstype': '/settings/address-types',
  '/person/addresstype/admin': '/settings/address-types',
  '/person/emailtype': '/settings/email-types',
  '/person/emailtype/admin': '/settings/email-types',
  '/person/phonetype': '/settings/phone-types',
  '/person/phonetype/admin': '/settings/phone-types',
  '/person/persontype': '/settings/person-types',
  '/person/persontype/admin': '/settings/person-types',
  '/person/personimagetype': '/person/image-types',
  '/person/personimagetype/admin': '/person/image-types',
  '/person/familyrelationshiptype': '/person/family-relationship-types',
  '/person/familyrelationshiptype/admin': '/person/family-relationship-types',
  '/person/personrelationshiptype': '/person/relationship-types',
  '/person/personrelationshiptype/admin': '/person/relationship-types',
  '/person/personattributetype': '/person/attribute-types',
  '/person/personattributetype/admin': '/person/attribute-types',
  '/manageaddrestype': '/settings/address-types',
  '/managephonetype': '/settings/phone-types',
  '/manageemailtype': '/settings/email-types',
  '/managepersontype': '/settings/person-types',

  // Person - Atributos e Integração
  '/person/personattribute': '/person/attributes',
  '/person/personattribute/admin': '/person/attributes',
  '/person/systemintegration': '/person/system-integrations',
  '/person/systemintegration/admin': '/person/system-integrations',
  '/person/personsystemintegration': '/person/person-integrations',
  '/person/personsystemintegration/admin': '/person/person-integrations',

  // Person - URLs Manage* (Menu Cadastro > Configurações)
  '/manageacess': '/person/access',
  '/manageclientgroup': '/person/client-groups',
  '/managehierarchicalorganization': '/person/hierarchical-org',
  '/managehierarchicallevel': '/person/hierarchical-levels',
  '/manageattributepeople': '/person/attributes',
  '/manageattributepersontype': '/person/attribute-types',
  '/managesystemintegration': '/person/system-integrations',
  '/managepersonsystemintegration': '/person/person-integrations',
  '/managebusinessarea': '/person/business-areas',
  '/managecorporationhierarchicalstructures': '/person/corporation-structures',
  '/sendtokenlot': '/person/send-token-batch',
  '/managepersonlatlng': '/person/latlng',
  '/manageimageperson': '/person/image-types',
  '/managerelationshiptypeperson': '/person/relationship-types',
  '/managerelationshipfamily': '/person/family-relationship-types',

  // ============================================
  // RESOURCE / RECURSO (Merchandising)
  // ============================================
  '/resource/resource': '/resource',
  '/resource/resource/admin': '/resource',
  '/resource/resource/status': '/resource',
  '/resource/resourceattr': '/resource',
  '/resource/resourceattr/admin': '/resource',
  '/resource/resourceattr/create': '/resource',
  '/resource/resourceattr/update': '/resource',
  '/resource/resourceupload': '/resource/uploads',
  '/resource/resourceupload/admin': '/resource/uploads',
  '/resource/resourceupload/resourceadmin': '/resource/uploads',
  '/resource/resourceupload/update': '/resource/uploads',
  '/resource/resourceupload/view': '/resource/uploads',
  '/resource/resourceupload/delete': '/resource/uploads',
  '/resource/resourceupload/uploadwidgetimage': '/resource/uploads',
  '/resource/resourcemap': '/resource/map',
  '/resource/resourcemap/index': '/resource/map',
  '/resource/resourceitem': '/resource',
  '/resource/resourceitem/admin': '/resource',
  '/resource/resourceitem/create': '/resource',
  '/resource/resourceitem/view': '/resource',
  '/resource/resourcecheckin': '/resource/checkins',
  '/resource/resourcecheckin/admin': '/resource/checkins',
  '/resource/resourcecheckin/pdf': '/resource/checkins',
  '/resource/resourcecheckin/view': '/resource/checkins',
  '/resource/resourcedashboardjasmine': '/resource/dashboard',
  '/resource/resourcedashboardjasmine/admin': '/resource/dashboard',
  '/resource/resourcedashboard': '/resource/dashboard',
  '/resource/resourcedashboard/admin': '/resource/dashboard',
  '/resourcemanager': '/resource',
  '/manageresource': '/resource',
  '/manageresourcecheckin': '/resource/checkins',
  '/manageresourceupload': '/resource/uploads',
  '/manageresourcedashboard': '/resource/dashboard',
  '/resource/resourceattrupload': '/resource/uploads',
  '/resource/resourceattrupload/admin': '/resource/uploads',

  // Resource Items específicos (por tipo de recurso)
  '/resource/resourceitem/admin/resource/price_shopping': '/resource/items/price_shopping',
  '/resource/resourceitem/admin/resource/ruptura': '/resource/items/ruptura',
  '/resource/resourceitem/admin/resource/rdv': '/resource/items/rdv',
  '/resource/resourceitem/admin/resource/share': '/resource/items/share',
  '/resource/resourceitem/admin/resource/execucao_no_pdv': '/resource/items/execucao_no_pdv',
  '/resource/resourceitem/admin/resource/material_pdv': '/resource/items/material_pdv',
  '/resource/resourceitem/admin/resource/analise_concorrencia': '/resource/items/analise_concorrencia',
  '/resource/resourceitem/admin/resource/atividades_diarias': '/resource/items/atividades_diarias',
  '/resource/resourceitem/admin/resource/acao_concorrencia': '/resource/items/acao_concorrencia',
  '/resource/resourceitem/admin/resource/ponto_extra': '/resource/items/ponto_extra',
  '/resource/resourceitem/admin/resource/coleta_preco': '/resource/items/coleta_preco',
  '/resource/resourceitem/admin/resource/relatorio_atividades': '/resource/items/relatorio_atividades',
  '/resource/resourceitem/admin/resource/relatorio_mix': '/resource/items/relatorio_mix',
  '/resource/resourceitem/admin/resource/mix_presenca': '/resource/items/mix_presenca',
  '/resource/resourceitem/admin/resource/shelf_life': '/resource/items/shelf_life',
  '/resource/resourceitem/admin/resource/shelflife': '/resource/items/shelflife',

  // ============================================
  // CONTENT / CONTEÚDO (CMS)
  // ============================================
  '/content/content': '/content',
  '/content/content/admin': '/content',
  '/content/content/create': '/content/create',
  '/content/content/update': '/content',
  '/content/content/view': '/content',
  '/content/content/delete': '/content',
  '/content/contentcategory': '/content/categories',
  '/content/contentcategory/admin': '/content/categories',
  '/content/contentcategory/update': '/content/categories',
  '/content/contentcategory/create': '/content/categories',
  '/contentmanager': '/content',
  '/contentcategory': '/content/categories',
  '/managecontentcategory': '/content/categories',

  // Content - Site (páginas dinâmicas)
  '/site/content': '/content',
  '/site/news': '/content',
  '/site/error': '/',

  // Content - Arquivos (Gerenciador de Arquivos)
  '/file/filemanager': '/files',
  '/file/filemanager/index': '/files',
  '/filemanager': '/files',
  '/files': '/files',

  // ============================================
  // CAMPAIGN / CAMPANHA
  // ============================================
  '/campaign/campaign': '/campaign',
  '/campaign/campaign/admin': '/campaign',
  '/campaign/campaign/create': '/campaign/create',
  '/campaign/campaign/view': '/campaign',
  '/campaign/campaign/update': '/campaign',
  '/campaign/campaigndashboard': '/campaign/dashboard',
  '/campaign/campaigndashboard/admin': '/campaign/dashboard',
  '/campaign/campaignprofileattribute': '/campaign/attributes',
  '/campaign/campaignprofileattribute/admin': '/campaign/attributes',
  '/campaign/campaignprofileattributetype': '/campaign/attribute-types',
  '/campaign/campaignprofileattributetype/admin': '/campaign/attribute-types',
  '/campaign/campaignjob': '/campaign/jobs',
  '/campaign/campaignjob/admin': '/campaign/jobs',
  '/campaign/campaignimportjob': '/campaign/import-jobs',
  '/campaign/campaignimportjob/admin': '/campaign/import-jobs',
  '/campaign/campaignterm': '/campaign/terms',
  '/campaign/campaignterm/admin': '/campaign/terms',
  '/campaign/campaignresult': '/campaign/results',
  '/campaign/campaignresult/admin': '/campaign/results',
  '/campaign/campaigncredit': '/campaign/credits',
  '/campaign/campaigncredit/admin': '/campaign/credits',
  '/managecampaign': '/campaign',
  '/configurationsregister': '/campaign',
  '/configurationscampaign': '/campaign',
  '/managecampaignattribute': '/campaign/attributes',
  '/managetermattribute': '/campaign/terms',
  '/managescoreconfig': '/campaign/modalities',
  '/managemodality': '/campaign/modalities',
  '/managetermattributetype': '/campaign/terms',
  '/managevariabletype': '/campaign/modalities',
  '/managecredit': '/campaign/credits',
  '/dashboardcampaign': '/campaign/dashboard',
  '/reversescorebatch': '/campaign/score-lots/reversal',
  '/exportcampaigndata': '/reports/campaign-data',
  '/managecampaignjob': '/campaign/jobs',
  '/importcampaign': '/campaign/import-jobs',
  '/managecampaignimportjob': '/campaign/import-jobs',
  '/manageresult': '/campaign/results',
  '/manageterm': '/campaign/terms',

  // ============================================
  // BUDGET / ORÇAMENTO
  // ============================================
  '/budget/budget': '/budget',
  '/budget/budget/admin': '/budget',
  '/budget/budget/manage': '/budget',
  '/budget/budgetcostcenter': '/budget/cost-centers',
  '/budget/budgetcostcenter/admin': '/budget/cost-centers',
  '/budget/budgetcostcenter/listactions': '/budget/cost-centers',
  '/budget/budgetapproval': '/budget/approval',
  '/budget/budgetapproval/admin': '/budget/approval',
  '/budget/budgetapproval/pending': '/budget/approval',
  '/budget/budgettransaction': '/budget/transactions',
  '/budget/budgettransaction/admin': '/budget/transactions',
  '/budget/budgettransaction/history': '/budget/transactions',
  '/managebudget': '/budget',
  '/managebudgetapproval': '/budget/approval',
  '/managebudgettransaction': '/budget/transactions',

  // ============================================
  // STORE / LOJA (E-commerce)
  // ============================================
  '/store/store': '/store',
  '/store/store/admin': '/store',
  '/store/product': '/store',
  '/store/product/admin': '/store',
  '/store/product/manageimage': '/store',
  '/store/producttype': '/store',
  '/store/producttype/admin': '/store',
  '/store/productcategory': '/store',
  '/store/productcategory/admin': '/store',
  '/store/productattr': '/store',
  '/store/productattr/admin': '/store',
  '/store/producttyperesource': '/store',
  '/store/producttyperesource/admin': '/store',
  '/store/catalog': '/store',
  '/store/cart': '/cart',
  '/store/checkout': '/checkout',
  '/store/sales': '/sales',
  '/store/tracking': '/tracking',
  '/managestore': '/store',

  // Store - URLs do menu Loja (newManage*)
  '/newmanagestore': '/store',
  '/newmanageconfigproducts': '/product',
  '/newmanageattrproducttypestore': '/product/types',
  '/newmanageshipmentmethod': '/settings/shipment-methods',
  '/newmanagepaymentmethod': '/settings/payment-methods',
  '/newmanagesales': '/sales',

  // Store - Importação/Exportação de imagens
  '/exportimagemproductcustom': '/product/export-images',
  '/importproductimage': '/product/import-images',
  '/importlistproductandexportimagefile': '/product/import-images',

  // Store - Loja de Merchandising (conteúdo dinâmico)
  '/loja-de-merchandising': '/store',

  // Store - Vendas
  '/sale/sale': '/sales',
  '/sale/sale/admin': '/sales',
  '/sale/salestatus': '/sales',
  '/sale/salestatus/admin': '/sales',
  '/sale/salepaymentmethod': '/settings/payment-methods',
  '/sale/salepaymentmethod/admin': '/settings/payment-methods',
  '/sale/saleshipmentmethod': '/settings/shipment-methods',
  '/sale/saleshipmentmethod/admin': '/settings/shipment-methods',

  // ============================================
  // PRODUCT / PRODUTO (Catálogo)
  // ============================================
  '/newmanageproducts': '/product',
  '/newmanageproductcategory': '/product/categories',
  '/manageproductbrand': '/product/brands',
  '/manageproductfamily': '/product/families',
  '/newmanageproducttype': '/product/types',
  '/manageproductimage': '/product/images',
  '/newmanageproductattr': '/product/attributes',

  // ============================================
  // SEGMENTATION / SEGMENTAÇÃO
  // ============================================
  '/segmentregion/region': '/segmentation/regions',
  '/segmentregion/region/admin': '/segmentation/regions',
  '/segmentregion/segment': '/segmentation/segments',
  '/segmentregion/segment/admin': '/segmentation/segments',
  '/manageregion': '/segmentation/regions',
  '/managesegment': '/segmentation/segments',

  // ============================================
  // RANKING
  // ============================================
  '/ranking/ranking': '/ranking',
  '/ranking/ranking/admin': '/ranking',
  '/ranking/ranking/create': '/ranking/create',
  '/ranking/ranking/view': '/ranking',
  '/ranking/ranking/update': '/ranking',
  '/ranking/rankinggeneratev2': '/ranking/generate',
  '/ranking/rankinggeneratev2/generate': '/ranking/generate',
  '/ranking/rankinggeneratev2/admin': '/ranking/generate',
  '/ranking/rankingjob': '/ranking/jobs',
  '/ranking/rankingjob/admin': '/ranking/jobs',
  '/ranking/rankingjob/create': '/ranking/jobs',
  '/ranking/rankingjob/update': '/ranking/jobs',
  '/manageranking': '/ranking',
  '/managerankinggenerate': '/ranking/generate',
  '/managerankingjob': '/ranking/jobs',
  '/generateranking': '/ranking/generate',

  // ============================================
  // SCORE / PONTUAÇÃO
  // ============================================
  '/score/scoreresult': '/score',
  '/score/scoreresult/admin': '/score',
  '/score/scoreresult/showgrid': '/score',
  '/score/scorecredit': '/score',
  '/score/scorecredit/admin': '/score',
  '/score/scorecredit/generatecredit': '/score',
  '/score/scoremodality': '/score',
  '/score/scoremodality/admin': '/score',
  '/score/scoreconfig': '/score',
  '/score/scoreconfig/admin': '/score',
  '/score/scorevariabletype': '/score',
  '/score/scorevariabletype/admin': '/score',
  '/score/generateresults': '/score',
  '/score/generateresults/generateresults': '/score',
  '/score/generateresultsv2': '/score',
  '/score/generateresultsv2/generateresults': '/score',

  // ============================================
  // TERM / TERMO
  // ============================================
  '/term/term': '/term',
  '/term/term/admin': '/term',
  '/term/term/create': '/term',
  '/term/term/view': '/term',
  '/term/term/update': '/term',
  '/term/term/delete': '/term',
  '/term/termattributetype': '/term',
  '/term/termattributetype/admin': '/term',
  '/term/termattribute': '/term',
  '/term/termattribute/admin': '/term',

  // ============================================
  // CONTACT / CONTATO (CRM) — motor tmf_contact removido (TH-56761) → SAC
  // ============================================
  '/contact/contact': '/sac',
  '/contact/contact/admin': '/sac',
  '/contact/contact/create': '/sac',
  '/contact/contact/update': '/sac',
  '/contact/contact/delete': '/sac',
  '/contact/contactstatus': '/sac',
  '/contact/contactstatus/admin': '/sac',
  '/contact/contactstatus/create': '/sac',
  '/contact/contactpriority': '/sac',
  '/contact/contactpriority/admin': '/sac',
  '/contact/contactpriority/create': '/sac',
  '/contact/contactcategory': '/sac',
  '/contact/contactcategory/admin': '/sac',
  '/contact/contactcategory/create': '/sac',
  '/contact/contactchannel': '/sac',
  '/contact/contactchannel/admin': '/sac',
  '/contact/contactchannel/create': '/sac',
  '/contact/contactattributetype': '/sac',
  '/contact/contactattributetype/admin': '/sac',
  '/contact/contactattribute': '/sac',
  '/contact/contactattribute/admin': '/sac',
  '/managecontactstatus': '/sac',
  '/managecontactpriority': '/sac',
  '/managecontactcategory': '/sac',
  '/managecontactchannel': '/sac',
  '/managecontact': '/sac',
  '/managecontactattribute': '/sac',
  '/managecontactattributetype': '/sac',
  '/managestatus': '/sac',
  '/managepriority': '/sac',
  '/managechannel': '/sac',
  '/managecategory': '/sac',
  '/sentnotificationpush': '/sac',

  // ============================================
  // IMPORT / IMPORTAÇÃO
  // ============================================
  '/import/importfile': '/import/upload',
  '/import/importfile/importfile': '/import/upload',
  '/import/importfile/downloadexample': '/import/upload',
  '/import/import': '/import/files',
  '/import/import/admin': '/import/files',
  '/import/importlog': '/import/logs',
  '/import/importlog/showlogs': '/import/logs',
  '/import/importlog/admin': '/import/logs',
  '/import/importtype': '/import/types',
  '/import/importtype/admin': '/import/types',
  '/import/importtype/create': '/import/types',
  '/import/importvalidation': '/import/validations',
  '/import/importvalidation/admin': '/import/validations',
  '/import/importvalidation/create': '/import/validations',
  '/import/importconvert': '/import/types',
  '/import/importconvert/admin': '/import/types',
  '/import/importfield': '/import/fields',
  '/import/importfield/admin': '/import/fields',
  '/import/importfield/create': '/import/fields',
  '/import/importcampaign': '/import/upload',
  '/import/importcampaign/importcampaign': '/import/upload',
  '/import/importcampaign/downloadexample': '/import/upload',
  '/import/importproduct': '/import/upload',
  '/import/importproduct/image': '/import/upload',
  '/import/importjob': '/import/files',
  '/import/importjob/admin': '/import/files',
  '/manageimport': '/import',
  '/manageimporttype': '/import/types',
  '/manageimportfield': '/import/fields',
  '/manageimportvalidation': '/import/validations',
  '/importations': '/import',
  '/configurationsimport': '/import',
  '/manageimportfields': '/import/fields',
  '/manageimportconvert': '/import/types',
  '/importfile': '/import/upload',
  '/manageimportjob': '/import/files',
  '/showimportlogs': '/import/logs',

  // ============================================
  // EXPORT / EXPORTAÇÃO
  // ============================================
  '/export/exporthierarchical': '/export',
  '/export/exporthierarchical/export': '/export',

  // ============================================
  // REPORT / RELATÓRIOS
  // ============================================
  '/report/report': '/reports',
  '/report/report/admin': '/reports',
  '/report/reportfavoriteimage': '/reports',
  '/report/reportfavoriteimage/admin': '/reports',
  '/reports/access': '/reports/access-log',
  '/reports/access/index': '/reports/access-log',
  '/reports/accesslog': '/reports/access-log',
  '/reports/accesslog/admin': '/reports/access-log',
  '/reports/contentaccesslog': '/reports/content-access',
  '/reports/contentaccesslog/admin': '/reports/content-access',
  '/reports/auditlog': '/reports/audit-log',
  '/reports/auditlog/admin': '/reports/audit-log',
  '/reports/persondata': '/reports/person-data',
  '/reports/persondata/admin': '/reports/person-data',
  '/reports/personhierarchy': '/reports/person-hierarchy',
  '/reports/personhierarchy/admin': '/reports/person-hierarchy',
  '/reports/personsegments': '/reports/person-segments',
  '/reports/personsegments/admin': '/reports/person-segments',
  '/reports/campaigndata': '/reports/campaign-data',
  '/reports/campaigndata/admin': '/reports/campaign-data',
  '/reports/participants': '/campaign/participants',
  '/reports/participants/admin': '/campaign/participants',
  '/reports/sales': '/reports/sales',
  '/reports/sales/admin': '/reports/sales',
  '/reports/products': '/reports/products',
  '/reports/products/admin': '/reports/products',
  '/reports/inventory': '/store/inventory-report',
  '/reports/inventory/admin': '/store/inventory-report',
  '/reports/productsales': '/reports/product-sales',
  '/reports/productsales/admin': '/reports/product-sales',
  '/reports/budget': '/budget/report',
  '/reports/budget/admin': '/budget/report',
  '/reports/costcenters': '/reports/cost-centers',
  '/reports/costcenters/admin': '/reports/cost-centers',
  '/reports/transactions': '/reports/transactions',
  '/reports/transactions/admin': '/reports/transactions',
  '/logincontentaccessreport': '/reports/content-access',
  '/report-client': '/reports',
  '/loginaccessreport': '/reports/access-log',
  '/rel_budget_base': '/budget/report',
  '/relatorio-de-participantes': '/campaign/participants',
  '/relatorio-de-perfil-de-acesso': '/reports',
  '/persondatareport': '/reports/person-data',
  '/relatorio-de-produtos': '/reports/products',
  '/exporthierarchical': '/reports/person-hierarchy',
  '/relatorio-de-sellin': '/reports/sales',
  '/relatorio-de-sellout': '/reports/sales',
  '/relatorio-de-vinculos-de-pf-x-pj': '/reports',
  '/managereportmkf': '/reports',
  '/relatorio-de-shopping-de-preco': '/reports',
  // Removido: duplicado em Resource items (linha 970)
  // '/relatorio-de-atividades': '/reports',
  '/relatorio-de-mix-presenca': '/reports',
  '/relatorio-de-ruptura1': '/reports',
  '/relatorio-de-market-share': '/reports',

  // ============================================
  // QUIZ / AVALIAÇÃO
  // ============================================
  '/quiz/quiz': '/quiz',
  '/quiz/quiz/admin': '/quiz',
  '/quiz/quiz/create': '/quiz/create',
  '/quiz/quiz/view': '/quiz',
  '/quiz/quiz/update': '/quiz',
  '/quiz/quizconfig': '/quiz/config',
  '/quiz/quizconfig/admin': '/quiz/config',
  '/quiz/quizexecution': '/quiz/executions',
  '/quiz/quizexecution/admin': '/quiz/executions',
  '/quiz/quizanswer': '/quiz/answers',
  '/quiz/quizanswer/admin': '/quiz/answers',
  '/quiz/quizanswer/review': '/quiz/answers',
  '/quiz/quizreport': '/quiz/reports',
  '/quiz/quizreport/admin': '/quiz/reports',
  '/quiz/quizreport/stats': '/quiz/reports',
  '/managequiz': '/quiz',
  '/managetest': '/quiz',
  '/menuquiz': '#',  // Grupo de sub-menus (não conflitar com /quiz do Avaliação)
  '/menucertification': '/quiz',
  '/managetestconfig': '/quiz/config',
  '/manageexecution': '/quiz/executions',
  '/indexerror': '/quiz/reports',
  '/testreporterrorbycategory': '/quiz/reports',
  '/testreportquestionbypeople': '/quiz/reports',
  '/testreport': '/quiz/reports',
  '/reportquiz': '/quiz/reports',
  '/quizanswervalue': '/quiz/answers',

  // ============================================
  // TEST / TESTES (Unificado com Quiz/Avaliações)
  // ============================================
  '/test/test': '/quiz',
  '/test/test/admin': '/quiz',
  '/test/testconfig': '/quiz/config',
  '/test/testconfig/admin': '/quiz/config',
  '/test/execution': '/quiz/executions',
  '/test/execution/admin': '/quiz/executions',
  '/test/testreport': '/quiz/reports',
  '/test/testreport/admin': '/quiz/reports',
  '/test/testreport/indexerror': '/quiz/reports',
  '/test/testreport/errorbycategory': '/quiz/reports',
  '/test/testreport/questionbypeople': '/quiz/reports',

  '/merchandising': '#',  // Grupo de sub-menus (não conflitar com /resource)
  '/execucao-do-pdv': '/resource/items/execucao_no_pdv',
  '/atividades-diarias': '/resource/items/atividades_diarias',
  '/acao-da-concorrencia-1': '/resource/items/acao_concorrencia',
  '/acao-da-concorrencia': '/resource/items/acao_concorrencia',
  '/dashboardcheckin': '/resource/dashboard',
  '/resourcecheckinmanage': '/resource/checkins',
  '/material-do-pdv': '/resource/items/material_pdv',
  '/ponto-extra1': '/resource/items/ponto_extra',
  '/ponto-extra': '/resource/items/ponto_extra',
  '/coleta-de-preco': '/resource/items/coleta_preco',
  '/coleta-preco': '/resource/items/coleta_preco',
  '/relatorio-de-atividades': '/resource/items/relatorio_atividades',
  '/relatorio-atividades': '/resource/items/relatorio_atividades',
  '/relatorio-de-mix': '/resource/items/mix_presenca',
  '/relatorio-mix': '/resource/items/mix_presenca',
  '/mix-presenca': '/resource/items/mix_presenca',
  '/ruptura': '/resource/items/ruptura',
  '/share': '/resource/items/share',
  '/shelf-life1': '/resource/items/shelf_life',
  '/shelf-life': '/resource/items/shelf_life',
  '/shelflife': '/resource/items/shelflife',
  '/manageresourceattribute': '/resource/attributes',
  '/resourcemap': '/resource/map',

  // ============================================
  // DRAWING / SORTEIOS
  // ============================================
  '/drawing/drawing': '/drawing',
  '/drawing/drawing/admin': '/drawing',
  '/drawing/drawing/create': '/drawing',
  '/drawing/drawing/view': '/drawing',
  '/drawing/drawing/update': '/drawing',
  '/drawing/drawing/execute': '/drawing',
  '/drawing/drawingresult': '/drawing/results',
  '/drawing/drawingresult/admin': '/drawing/results',
  '/drawing/drawingwinner': '/drawing/results',
  '/drawing/drawingwinner/admin': '/drawing/results',
  '/managedrawing': '/drawing',
  '/managedrawingresult': '/drawing/results',
  '/raffle': '/drawing',
  '/manageraffleresults': '/drawing/results',
  '/manageraffles': '/drawing',

  // ============================================
  // ETL (Integração de Dados)
  // ============================================
  '/etl/etl': '/etl',
  '/etl/etl/admin': '/etl',

  // ============================================
  // PUSH NOTIFICATION
  // ============================================
  '/pushnotification/notification': '/notifications',
  '/pushnotification/notification/admin': '/notifications',
  '/pushnotification/notification/notify': '/notifications',

  // ============================================
  // CHECKIN (Mapas e visualizações)
  // ============================================
  '/checkin': '/checkin/map',
  '/checkin/index': '/checkin/map',
  '/checkin/admin': '/checkin/map',
  '/managecheckin': '/checkin/map',
  '/checkinmap': '/checkin/map',
  '/checkin-map': '/checkin/map',

  // ============================================
  // DASHBOARD
  // ============================================
  '/dashboard/checkin': '/checkin/map',
  '/dashboard/checkin/detail': '/checkin/map',
  '/dashboard/checkin/realtime': '/checkin/map',
  '/dashboard/campaign': '/dashboard',
  '/dashboard/campaign/detail': '/dashboard',

  // ============================================
  // ADMIN (Ferramentas de administração)
  // ============================================
  '/admin': '/settings',
  '/admin/schema-editor': '/admin/schema-editor',

  // ============================================
  // SYSTEM / SISTEMA (Admin)
  // ============================================
  '/system/config': '/settings/configurations',
  '/system/config/admin': '/settings/configurations',
  '/system/theme': '/settings/themes',
  '/system/theme/setthemevalues': '/settings/themes',
  '/fullconfigurations': '/settings/configurations',
  '/customconfigurations': '/settings/configurations',
  '/managethemesconfig': '/settings/themes',
  '/managecurrencyrate': '/settings/currencies',
  '/manageetl': '/settings/etl',
  '/gerenciar_idiomas': '/settings/languages',
  '/logemail': '/communication/email-logs',
  '/system/emaillog': '/communication/email-logs',
  '/system/emaillog/admin': '/communication/email-logs',
  '/managemessages': '/settings/languages',
  '/managemenu': '/settings/menu-types',
  '/manageaccessprofile': '/settings/access/profiles',

  // ============================================
  // ACCESS / ACESSO (Admin)
  // ============================================
  '/access/login': '/login',
  '/access/login/admin': '/person/access',
  '/access/login/sendtokenlot': '/person/send-token-batch',
  '/access/login/update': '/person/access',
  '/access/menutype': '/settings/menu-types',
  '/access/menutype/admin': '/settings/menu-types',
  '/access/menutype/create': '/settings/menu-types',
  '/access/authitem': '/settings/auth-items',
  '/access/authitem/admin': '/settings/auth-items',
  '/access/authitem/create': '/settings/auth-items',
  '/managemenutype': '/settings/menu-types',
  '/manageauthitem': '/settings/auth-items',

  // ============================================
  // CURRENCY / MOEDAS
  // ============================================
  '/currency/currency': '/settings/currencies',
  '/currency/currency/admin': '/settings/currencies',
  '/currency/currency/create': '/settings/currencies',
  '/currency/currencyrate': '/settings/currencies',
  '/currency/currencyrate/admin': '/settings/currencies',
  '/managecurrency': '/settings/currencies',

  // ============================================
  // MESSAGES / INTERNACIONALIZAÇÃO
  // ============================================
  '/messages/messagesource': '/settings/languages',
  '/messages/messagesource/admin': '/settings/languages',
  '/messages/language': '/settings/languages',
  '/messages/language/admin': '/settings/languages',
  '/managelanguage': '/settings/languages',

  // ============================================
  // AUTH / LOGIN
  // ============================================
  '/site/login': '/login',
  '/site/logout': '/login',

  // ============================================
  // PROFILE / PERFIL
  // ============================================
  '/profile': '/profile',

  // ============================================
  // DOCUMENTAÇÃO LEGADO (páginas de ajuda do sistema antigo)
  // Mapeadas para /page/{code} (conteúdo dinâmico)
  // ============================================
  '/doc_gerenciar_pessoas': '/page/doc_gerenciar_pessoas',
  '/doc_gerenciar_checkin': '/page/doc_gerenciar_checkin',
  '/doc_grupo_cliente': '/page/doc_grupo_cliente',
  '/doc_roteiro': '/page/doc_roteiro',
  '/doc_acao_concorrencia': '/page/doc_acao_concorrencia',
  '/doc_atividade_diaria': '/page/doc_atividade_diaria',
  '/doc_controle_de_display': '/page/doc_controle_de_display',
  '/doc_material_pdv': '/page/doc_material_pdv',
  '/doc_ponto_extra': '/page/doc_ponto_extra',
  '/doc_shelf_life': '/page/doc_shelf_life',
  '/ruptura-doc': '/page/ruptura-doc',
  '/template---docs-sidebar': '/page/template---docs-sidebar',
  '/dashboard-check-in': '/checkin/map',

  // ============================================
  // URLs AVULSAS LEGADO
  // ============================================
  '/gerenciar-regioes': '/segmentation/regions',
  '/gerenciar-segmento': '/segmentation/segments',
  '/painel-ao-vivo': '/',
  '/leads_1': '/sac', // motor tmf_contact removido (TH-56761) → SAC
  '/share1': '/',
};

// ============================================================
// EXPORTAR FUNÇÕES DE MAPEAMENTO DE URLs
// Para uso em outros componentes (widgets, etc.)
// ============================================================

/**
 * Verifica se a URL indica um resource item e extrai informações.
 *
 * @param url URL no formato Yii
 * @returns Objeto indicando se é resource e o código (se presente na URL)
 *
 * Padrões detectados:
 * - /resource/resourceitem/admin/resource/{code} → isResource: true, code: {code}
 * - /resource/resourceitem/admin → isResource: true, useContentCode: true
 * - /{numero} (ID de content) → isResource: true, useContentCode: true
 */
export function detectResourceUrl(url: string): {
  isResource: boolean;
  code?: string;
  useContentCode?: boolean;
} {
  if (!url) {
    return { isResource: false };
  }

  const cleanUrl = url.toLowerCase().replace(/^\/+|\/+$/g, '');

  // Padrão 1: resource/resourceitem/admin/resource/{code}
  // O código do resource está na URL
  const resourceItemWithCodePattern = /^resource\/resourceitem\/admin\/resource\/(.+)$/;
  const matchWithCode = cleanUrl.match(resourceItemWithCodePattern);
  if (matchWithCode) {
    return {
      isResource: true,
      code: matchWithCode[1],
    };
  }

  // Padrão 2: resource/resourceitem/admin (sem código)
  // Deve usar o código do content
  const resourceItemAdminPattern = /^resource\/resourceitem\/admin$/;
  if (resourceItemAdminPattern.test(cleanUrl)) {
    return {
      isResource: true,
      useContentCode: true,
    };
  }

  // Padrão 3: ID numérico puro (ex: /697)
  // É o ID do content no Yii - deve usar o código do content para resource
  const numericIdPattern = /^(\d+)$/;
  if (numericIdPattern.test(cleanUrl)) {
    return {
      isResource: true,
      useContentCode: true,
    };
  }

  return { isResource: false };
}

/**
 * Processa URL convertendo para rota React.
 * Exportado para uso em outros componentes como MkfBlock, MkfBanner, etc.
 *
 * @param url URL no formato Yii ou React
 * @returns Objeto com path (rota React) e isExternal (se abre em nova aba)
 */
export function processYiiUrl(url: string): { path: string; isExternal: boolean; isGroup: boolean } {
  // URL vazia ou '#' ou '/#' indica grupo/container (sem navegação)
  if (!url || url === '#' || url === '/#') {
    return { path: '#', isExternal: false, isGroup: true };
  }

  // URL externa (http:// ou https://) - abre em nova aba
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return { path: url, isExternal: true, isGroup: false };
  }

  // Usar mapeamento de URLs Yii para React
  const { path, isGroup } = mapLegacyUrl(url);
  return { path, isExternal: false, isGroup };
}

/**
 * Parse URL Yii em partes (module, controller, action).
 *
 * Formatos suportados:
 * - /controller/action           → { module: controller, controller: controller, action: action }
 * - /module/controller/action    → { module: module, controller: controller, action: action }
 * - /controller                  → { module: controller, controller: controller, action: 'index' }
 *
 * @param url URL no formato Yii
 * @returns Partes da URL parseadas
 */
function parseYiiUrl(url: string): { module: string; controller: string; action: string } | null {
  // Remove leading/trailing slashes and normalize
  const cleanUrl = url.toLowerCase().replace(/^\/+|\/+$/g, '');

  if (!cleanUrl) {
    return null;
  }

  const parts = cleanUrl.split('/');

  if (parts.length === 1) {
    // /controller → lista
    return { module: parts[0], controller: parts[0], action: 'index' };
  }

  if (parts.length === 2) {
    // /controller/action
    return { module: parts[0], controller: parts[0], action: parts[1] };
  }

  if (parts.length >= 3) {
    // /module/controller/action (ou mais partes)
    return { module: parts[0], controller: parts[1], action: parts[2] };
  }

  return null;
}

/**
 * Detecta padrões especiais de URL que precisam de tratamento dinâmico.
 * Retorna a rota React correspondente ou null se não for um padrão especial.
 *
 * Padrões suportados:
 * - /resource/resourceitem/admin/resource/{code} → /resource/items/{code}
 *
 * NOTA: IDs numéricos puros (ex: /697) são IDs de content, não de resource.
 * Para esses casos, o MkfBlock usa o `code` do content diretamente.
 */
function detectSpecialPattern(url: string): string | null {
  // Normalizar URL
  const cleanUrl = url.toLowerCase().replace(/^\/+|\/+$/g, '');

  // Padrão: resource/resourceitem/admin/resource/{code}
  // Converte para: /resource/items/{code}
  const resourceItemPattern = /^resource\/resourceitem\/admin\/resource\/(.+)$/;
  const resourceItemMatch = cleanUrl.match(resourceItemPattern);
  if (resourceItemMatch) {
    const resourceCode = resourceItemMatch[1];
    return `/resource/items/${resourceCode}`;
  }

  // Padrão: resource/resourceitem/admin (sem código específico)
  // Indica que é um content de resource - retorna marcador especial
  const resourceItemAdminPattern = /^resource\/resourceitem\/admin$/;
  if (resourceItemAdminPattern.test(cleanUrl)) {
    return '__USE_CONTENT_CODE_FOR_RESOURCE__';
  }

  return null;
}

/**
 * Deduz a rota React baseada nos padrões Yii (controller/action).
 *
 * @param url URL no formato Yii
 * @returns Rota React deduzida ou null se não conseguir deduzir
 */
function deduceReactRoute(url: string): string | null {
  // Primeiro, verificar padrões especiais (resource items, IDs numéricos, etc.)
  const specialRoute = detectSpecialPattern(url);
  if (specialRoute) {
    return specialRoute;
  }

  const parsed = parseYiiUrl(url);

  if (!parsed) {
    return null;
  }

  const { module, controller, action } = parsed;

  // 1. Tenta encontrar a rota base pelo módulo
  const baseRoute = MODULE_TO_REACT_ROUTE[module];

  if (!baseRoute) {
    // Módulo não mapeado - tenta usar o nome do módulo como rota
    const fallbackRoute = `/${module}`;

    // Verifica se a rota existe no React
    if (REACT_ROUTES.has(fallbackRoute)) {
      return fallbackRoute;
    }

    return null;
  }

  // 2. Aplica o sufixo baseado na action
  const suffix = ACTION_TO_SUFFIX[action];

  if (suffix !== undefined) {
    const fullRoute = `${baseRoute}${suffix}`;

    // Verifica se a rota existe
    if (REACT_ROUTES.has(fullRoute)) {
      return fullRoute;
    }

    // Se a rota com sufixo não existe, retorna a base
    if (REACT_ROUTES.has(baseRoute)) {
      return baseRoute;
    }
  }

  // 3. Tenta deduzir baseado no controller específico
  // Ex: /person/addresstype/admin → /settings/address-types
  if (controller !== module) {
    // Controllers específicos mapeados para subrotas
    const controllerMappings: Record<string, Record<string, string>> = {
      person: {
        addresstype: '/settings/address-types',
        phonetype: '/settings/phone-types',
        emailtype: '/settings/email-types',
        persontype: '/settings/person-types',
        personimagetype: '/person/image-types',
        familyrelationshiptype: '/person/family-relationship-types',
        personrelationshiptype: '/person/relationship-types',
        personattributetype: '/person/attribute-types',
        personattribute: '/person/attributes',
        systemintegration: '/person/system-integrations',
        personsystemintegration: '/person/person-integrations',
        clientgroup: '/person/client-groups',
        register: '/person',
        corporation: '/person/hierarchical-org',
      },
      store: {
        product: '/product',
        producttype: '/product/types',
        productcategory: '/product/categories',
        productattr: '/product',
        catalog: '/store',
        cart: '/cart',
        checkout: '/checkout',
      },
      sale: {
        sale: '/sales',
        salestatus: '/sales',
        salepaymentmethod: '/settings/payment-methods',
        saleshipmentmethod: '/settings/shipment-methods',
      },
      segmentregion: {
        region: '/segmentation/regions',
        segment: '/segmentation/segments',
      },
      budget: {
        budget: '/budget',
        budgetcostcenter: '/budget/cost-centers',
      },
    };

    const moduleControllers = controllerMappings[module];
    if (moduleControllers && moduleControllers[controller]) {
      return moduleControllers[controller];
    }
  }

  // 4. Retorna a rota base se existir
  if (REACT_ROUTES.has(baseRoute)) {
    return baseRoute;
  }

  return null;
}

/**
 * Transform legacy URL to React route.
 * Returns the mapped route or original URL if no mapping exists.
 *
 * IMPORTANTE: Frontend React é 100% desacoplado do Yii.
 * Todas as URLs são tratadas como rotas React (nunca redireciona para Yii).
 *
 * Estratégia de mapeamento (em ordem de prioridade):
 * 1. Mapeamento estático (LEGACY_URL_MAP) - para URLs específicas
 * 2. Dedução automática (deduceReactRoute) - baseado em padrões controller/action
 * 3. Fallback para rota original - pode resultar em 404 se não existir
 */
function mapLegacyUrl(url: string): { path: string; isLegacy: boolean; isGroup: boolean } {
  // Empty URL or '#' or '/#' indicates a group/container menu (no navigation)
  if (!url || url === '#' || url === '/#') {
    return { path: '#', isLegacy: false, isGroup: true };
  }

  // Normalizar barras duplas no início (ex: //communication → /communication)
  let cleanUrl = url;
  while (cleanUrl.startsWith('//')) {
    cleanUrl = cleanUrl.slice(1);
  }

  const normalizedUrl = cleanUrl.toLowerCase();

  // 1. Tenta mapeamento estático primeiro (maior prioridade)
  const mappedUrl = LEGACY_URL_MAP[normalizedUrl];
  if (mappedUrl) {
    return { path: mappedUrl, isLegacy: false, isGroup: mappedUrl === '#' };
  }

  // 1.5. Preservar URLs de conteúdo (/page/{code}) - são rotas React válidas
  // Essas URLs não devem ser processadas pelo deduceReactRoute
  if (normalizedUrl.startsWith('/page/') && normalizedUrl.length > 6) {
    return { path: cleanUrl, isLegacy: false, isGroup: false };
  }

  // 2. Verifica se a URL já é uma rota React válida ANTES de tentar deduzir
  // Isso evita que rotas como /person/access sejam erroneamente deduzidas como /person
  if (REACT_ROUTES.has(normalizedUrl)) {
    return { path: normalizedUrl, isLegacy: false, isGroup: false };
  }

  // 2.5. Verifica padrões dinâmicos de rotas React (com parâmetros)
  const matchesDynamicRoute = REACT_ROUTE_PATTERNS.some(pattern => pattern.test(normalizedUrl));
  if (matchesDynamicRoute) {
    return { path: normalizedUrl, isLegacy: false, isGroup: false };
  }

  // 3. Tenta dedução automática baseada em padrões Yii
  const deducedRoute = deduceReactRoute(cleanUrl);
  if (deducedRoute) {
    return { path: deducedRoute, isLegacy: false, isGroup: false };
  }

  // 4. URL não mapeada - tenta extrair módulo e usar como rota base
  const parsed = parseYiiUrl(cleanUrl);
  if (parsed) {
    const baseRoute = MODULE_TO_REACT_ROUTE[parsed.module];
    if (baseRoute && REACT_ROUTES.has(baseRoute)) {
      return { path: baseRoute, isLegacy: false, isGroup: false };
    }
  }

  // 5. Fallback final: retorna a URL normalizada
  // Se não existir no React Router, usuário verá 404
  if (import.meta.env.DEV) console.warn(`[Menu] URL não mapeada: ${cleanUrl} - usando como está`);
  return { path: cleanUrl, isLegacy: false, isGroup: false };
}

/**
 * Transform MenuTreeNode to NavMenuItem.
 */
export function toNavMenuItem(node: MenuTreeNode): NavMenuItem {
  const isExternal = node.url.startsWith('http://') || node.url.startsWith('https://');
  const { path, isLegacy, isGroup } = mapLegacyUrl(node.url);

  return {
    id: node.id,
    label: node.label,
    path,
    icon: node.icon,
    target: node.target,
    isExternal,
    isLegacy,
    isGroup,
    children: node.children.map(toNavMenuItem),
    level: node.level,
    cssClass: node.cssClass,
  };
}

/**
 * Filter out empty groups (groups without children).
 */
function filterEmptyGroups(items: NavMenuItem[]): NavMenuItem[] {
  return items
    .map((item) => ({
      ...item,
      children: filterEmptyGroups(item.children),
    }))
    .filter((item) => {
      // Remove groups without children
      if (item.isGroup && item.children.length === 0) {
        return false;
      }
      return true;
    });
}

/**
 * Remove itens duplicados (mesmo path) no mesmo nível do menu.
 * Quando múltiplas URLs Yii mapeiam para a mesma rota React,
 * isso evita que vários itens apareçam como "ativos" simultaneamente.
 *
 * Regras de prioridade (mantém o primeiro encontrado nesta ordem):
 * 1. Itens que são grupos (têm children) têm prioridade
 * 2. Entre itens não-grupo, mantém o primeiro encontrado
 *
 * @param items Array de itens de menu
 * @returns Array sem itens duplicados
 */
function deduplicateMenuItems(items: NavMenuItem[]): NavMenuItem[] {
  const seenPaths = new Map<string, NavMenuItem>();

  for (const item of items) {
    const normalizedPath = item.path.toLowerCase();

    // Grupos sem path específico (path = '#') não são dedupados
    if (item.isGroup && item.path === '#') {
      seenPaths.set(`group_${item.id}`, item);
      continue;
    }

    const existing = seenPaths.get(normalizedPath);

    if (!existing) {
      // Primeiro item com esse path
      seenPaths.set(normalizedPath, item);
    } else {
      // Já existe um item com esse path - aplicar regras de prioridade
      // Prioridade 1: Grupos têm prioridade sobre não-grupos
      if (item.isGroup && !existing.isGroup) {
        seenPaths.set(normalizedPath, item);
      }
      // Prioridade 2: Se ambos são grupos ou ambos não-grupos, manter o primeiro
      // (não faz nada, mantém o existing)
    }
  }

  // Reconstruir array mantendo a ordem original dos itens mantidos
  const result: NavMenuItem[] = [];
  const keptItems = new Set(seenPaths.values());

  for (const item of items) {
    if (keptItems.has(item)) {
      // Recursivamente deduplicar children
      result.push({
        ...item,
        children: deduplicateMenuItems(item.children),
      });
    }
  }

  return result;
}

/**
 * Transform MenuTreeResponse to array of NavMenuItem.
 * Aplica deduplicação para evitar múltiplos itens com o mesmo path.
 */
export function toNavMenuItems(response: MenuTreeResponse): NavMenuItem[] {
  const items = response.tree.map(toNavMenuItem);
  const filtered = filterEmptyGroups(items);
  return deduplicateMenuItems(filtered);
}
